const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const PROXY_URL = `${SUPABASE_URL}/functions/v1/db-proxy`;

let _authGetter: (() => Promise<string>) | null = null;

export function setAuthTokenGetter(getter: () => Promise<string>) {
  _authGetter = getter;
}

async function getAuthToken(): Promise<string> {
  if (_authGetter) {
    return await _authGetter();
  }
  return SUPABASE_ANON_KEY;
}

async function proxyRequest(body: any): Promise<any> {
  const token = await getAuthToken();
  const res = await fetch(PROXY_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });

  const json = await res.json();
  if (!res.ok) {
    return { data: null, error: { message: json.error, code: json.code || 'PROXY_ERROR' }, count: null };
  }
  return { data: json.data, error: null, count: json.count ?? null };
}

class QueryBuilder {
  private _table: string;
  private _action: string;
  private _select: string = '*';
  private _filters: any[] = [];
  private _order: any[] = [];
  private _limit: number | null = null;
  private _data: any = null;
  private _head: boolean = false;
  private _count: string | null = null;

  constructor(table: string, action: string) {
    this._table = table;
    this._action = action;
  }

  select(columns?: string, opts?: { count?: string; head?: boolean }) {
    if (columns) this._select = columns;
    if (opts?.head) this._head = true;
    if (opts?.count) this._count = opts.count;
    if (this._action === 'insert' || this._action === 'update') {
      this._select = columns || '*';
    }
    return this;
  }

  insert(data: any) {
    this._action = 'insert';
    this._data = data;
    return this;
  }

  update(data: any) {
    this._action = 'update';
    this._data = data;
    return this;
  }

  delete() {
    this._action = 'delete';
    return this;
  }

  eq(column: string, value: any) { this._filters.push({ op: 'eq', column, value }); return this; }
  neq(column: string, value: any) { this._filters.push({ op: 'neq', column, value }); return this; }
  gt(column: string, value: any) { this._filters.push({ op: 'gt', column, value }); return this; }
  gte(column: string, value: any) { this._filters.push({ op: 'gte', column, value }); return this; }
  lt(column: string, value: any) { this._filters.push({ op: 'lt', column, value }); return this; }
  lte(column: string, value: any) { this._filters.push({ op: 'lte', column, value }); return this; }
  in(column: string, value: any[]) { this._filters.push({ op: 'in', column, value }); return this; }
  is(column: string, value: any) { this._filters.push({ op: 'is', column, value }); return this; }
  like(column: string, value: string) { this._filters.push({ op: 'like', column, value }); return this; }
  ilike(column: string, value: string) { this._filters.push({ op: 'ilike', column, value }); return this; }
  not(column: string, op: string, value: any) { this._filters.push({ op: 'not', column, op2: op, value }); return this; }
  or(value: string) { this._filters.push({ op: 'or', value }); return this; }
  contains(column: string, value: any) { this._filters.push({ op: 'contains', column, value }); return this; }

  order(column: string, opts?: { ascending?: boolean; referencedTable?: string }) {
    this._order.push({ column, ascending: opts?.ascending !== false, foreignTable: opts?.referencedTable });
    return this;
  }

  limit(count: number) { this._limit = count; return this; }

  single() { this._filters.push({ op: 'single' }); return this; }
  maybeSingle() { this._filters.push({ op: 'maybeSingle' }); return this; }

  then(resolve: (value: any) => any, reject?: (reason: any) => any): Promise<any> {
    const promise = this._execute();
    return promise.then(resolve, reject);
  }

  private async _execute() {
    return proxyRequest({
      action: this._action,
      table: this._table,
      select: this._select,
      filters: this._filters.length > 0 ? this._filters : undefined,
      order: this._order.length > 0 ? this._order : undefined,
      limit: this._limit,
      data: this._data,
      head: this._head || undefined,
      count: this._count || undefined,
    });
  }
}

export const dbProxy = {
  from(table: string) {
    return new ProxyTableBuilder(table);
  },
  rpc(fnName: string, params?: any) {
    return {
      then(resolve: (value: any) => any, reject?: (reason: any) => any) {
        return proxyRequest({ action: 'rpc', rpc: fnName, rpcParams: params }).then(resolve, reject);
      }
    };
  }
};

class ProxyTableBuilder {
  private _table: string;
  constructor(table: string) { this._table = table; }

  select(columns?: string, opts?: { count?: string; head?: boolean }) {
    const qb = new QueryBuilder(this._table, 'select');
    return qb.select(columns, opts);
  }

  insert(data: any) {
    const qb = new QueryBuilder(this._table, 'insert');
    qb.insert(data);
    return qb;
  }

  update(data: any) {
    const qb = new QueryBuilder(this._table, 'update');
    qb.update(data);
    return qb;
  }

  delete() {
    const qb = new QueryBuilder(this._table, 'delete');
    qb.delete();
    return qb;
  }
}
